import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Select } from "antd";
import LicenseIcon from "../../../assets/images/vectors/SideBarIcons/LicenseIcon";
import CommonButton from "../../../global/components/CommonButton";
import {
  calculateTotals,
  hidePriceLabels,
  iconMap,
  orderTypeOptions,
} from "../../../utils/Common";
import LicenseTypeIcon from "../../../assets/images/vectors/LicenseTypeIcon";
import DoorTypeIcon from "../../../assets/images/vectors/DoorTypeIcon";
import SiteTypeIcon from "../../../assets/images/vectors/SiteTypeIcon";
import { useMemo } from "react";

const LicenseConfirmForm = ({
  onConfirm,
  onCancel,
  mergedData,
  optionsData,
  licenseType,
  modelsOptions,
  siteTypeOptions,
  setLicenseType,
  licenseTypePrice,
  siteTypePrice = 0,
  doorPrice,
  form,
  loading,
  disableSelect,
  edit,
  originalLicenseData,
}) => {
  // Check if there are ANY changes at all - ADD THIS SECTION
  const hasAnyChanges = useMemo(() => {
    if (!edit || !originalLicenseData) return false;

    // Check license type change
    const originalModel = modelsOptions?.find(
      (m) => m.value === originalLicenseData.Model,
    );
    const currentModel = modelsOptions?.find(
      (m) => m.value === mergedData?.Model,
    );
    if (
      originalModel?.label !== currentModel?.label ||
      originalLicenseData.licenseTypePrice !== licenseTypePrice
    ) {
      return true;
    }

    // Check site type change
    const originalSite = siteTypeOptions?.find(
      (m) => m.value === originalLicenseData.SiteType,
    );
    const currentSite = siteTypeOptions?.find(
      (m) => m.value === mergedData?.SiteType,
    );
    if (
      originalSite?.label !== currentSite?.label ||
      originalLicenseData.siteTypePrice !== siteTypePrice
    ) {
      return true;
    }

    // Check door type change
    if (
      originalLicenseData.Type !== mergedData?.Type ||
      originalLicenseData.doorPrice !== doorPrice
    ) {
      return true;
    }

    // Check for added or removed options
    const currentKeys = Object.keys(optionsData).filter(
      (key) => !hidePriceLabels.includes(key),
    );
    const originalKeys = Object.keys(
      originalLicenseData.optionsData || {},
    ).filter((key) => !hidePriceLabels.includes(key));

    // Check if any items were added
    const addedItems = currentKeys.filter((key) => {
      const originalItem = originalLicenseData.optionsData?.[key];
      const currentItem = optionsData[key];

      if (!originalItem) return true;

      if (currentItem.type === "toggle") {
        return !originalItem.enabled && currentItem.enabled;
      }

      return false;
    });

    if (addedItems.length > 0) return true;

    // Check if any items were removed
    const removedItems = originalKeys.filter((key) => {
      const originalItem = originalLicenseData.optionsData[key];
      const currentItem = optionsData[key];

      if (!currentItem) return true;

      if (originalItem.type === "toggle") {
        return originalItem.enabled && !currentItem.enabled;
      }

      return false;
    });

    if (removedItems.length > 0) return true;

    // Check for quantity/price changes in existing items
    for (const key of currentKeys) {
      const originalItem = originalLicenseData.optionsData?.[key];
      const currentItem = optionsData[key];

      if (originalItem && currentItem) {
        if (currentItem.type === "input" || currentItem.type === "select") {
          const quantityDiff =
            currentItem.quantity - (originalItem.quantity || 0);
          if (quantityDiff !== 0) return true;

          const priceDiff = currentItem.price - (originalItem.price || 0);
          if (priceDiff !== 0) return true;
        }
      }
    }

    return false;
  }, [
    edit,
    originalLicenseData,
    mergedData,
    optionsData,
    licenseTypePrice,
    siteTypePrice,
    doorPrice,
    modelsOptions,
    siteTypeOptions,
  ]);

  // Helper to get option difference
  const getOptionDifference = (key, currentItem) => {
    if (!edit || !originalLicenseData?.optionsData) return null;

    const originalItem = originalLicenseData.optionsData[key];

    // If item didn't exist before, it's added
    if (!originalItem) {
      return {
        type: "added",
        quantity: currentItem.quantity || 1,
        price: currentItem.price,
      };
    }

    // For toggle items, check if enabled status changed
    if (currentItem.type === "toggle") {
      if (!originalItem.enabled && currentItem.enabled) {
        return { type: "added", quantity: 1, price: currentItem.price };
      }
      // Check for price change even if toggle status didn't change
      const priceDiff = currentItem.price - (originalItem.price || 0);
      if (Math.abs(priceDiff) > 0.01 && currentItem.enabled) {
        return {
          type: "price_changed",
          quantity: 1,
          priceDiff: Math.abs(priceDiff),
        };
      }
      return null;
    }

    // For input/select items, check quantity OR price change
    if (currentItem.type === "input" || currentItem.type === "select") {
      const quantityDiff = currentItem.quantity - (originalItem.quantity || 0);
      const priceDiff = currentItem.price - (originalItem.price || 0);

      // Check if either quantity or price changed
      if (quantityDiff !== 0 || Math.abs(priceDiff) > 0.01) {
        return {
          type:
            quantityDiff > 0
              ? "increased"
              : quantityDiff < 0
                ? "decreased"
                : "price_changed",
          quantityDiff: Math.abs(quantityDiff),
          priceDiff: Math.abs(priceDiff),
        };
      }
    }

    return null;
  };

  // Get model name
  const getModelName = () => {
    const model = modelsOptions?.find((m) => m.value === mergedData?.Model);
    return model?.label || "N/A";
  };

  const getSiteTypeName = () => {
    const siteType = siteTypeOptions?.find(
      (m) => m.value === mergedData?.SiteType,
    );
    return siteType?.label || "N/A";
  };

  const groupedOptions = useMemo(() => {
    // If edit mode with changes, consolidate everything into options
    if (hasAnyChanges && edit) {
      const options = [];
      const addedKeys = new Set();

      // Add current items
      Object.entries(optionsData).forEach(([key, val]) => {
        if (hidePriceLabels.includes(key)) return;
        options.push({ name: key, ...val });
        addedKeys.add(key);
      });

      // Add removed items - check if item was truly removed
      if (originalLicenseData?.optionsData) {
        Object.entries(originalLicenseData.optionsData).forEach(
          ([key, originalItem]) => {
            if (hidePriceLabels.includes(key)) return;

            const currentItem = optionsData[key];

            // Only add if the item doesn't exist in current optionsData
            // OR if it's a toggle that was disabled
            const isRemoved =
              !currentItem ||
              (originalItem.type === "toggle" &&
                originalItem.enabled &&
                currentItem?.type === "toggle" &&
                !currentItem.enabled);

            if (isRemoved && !addedKeys.has(key)) {
              options.push({
                name: key,
                ...originalItem,
                isRemoved: true,
              });
              addedKeys.add(key);
            }
          },
        );
      }

      return {
        options,
        device: [],
        video: [],
        mobile: [],
      };
    }

    // Normal mode - separate into groups
    const groups = {
      options: [],
      device: [],
      video: [],
      mobile: [],
    };

    const addedKeys = new Set();

    Object.entries(optionsData).forEach(([key, val]) => {
      if (hidePriceLabels.includes(key)) return;

      const item = { name: key, ...val };
      addedKeys.add(key);

      const optionsCards = [
        "REST API",
        "Log API",
        "Credential Invitation",
        "Visitor Invitation",
        "Gemini",
        "Mobile App",
        "Single Sign On",
        "Badge",
      ];

      const deviceCards = [
        "Lockset",
        "Facegate",
        "OSDP Device",
        "Trilogy Lockset",
        "Intercom",
        "Directory Extension",
      ];

      const mobileCredentialCards = [
        "Sicunet Mobile",
        "Wavelynx Mobile",
        "Farpointe Mobile",
        "Mobile Credential",
      ];

      if (optionsCards.includes(key)) {
        groups.options.push(item);
      } else if (deviceCards.includes(key)) {
        groups.device.push(item);
      } else if (key === "Channel") {
        groups.video.push(item);
      } else if (mobileCredentialCards.includes(key)) {
        groups.mobile.push(item);
      } else {
        groups.options.push(item);
      }
    });

    // Add removed items in normal mode
    if (edit && originalLicenseData?.optionsData) {
      const optionsCards = [
        "REST API",
        "Log API",
        "Credential Invitation",
        "Visitor Invitation",
        "Gemini",
        "Mobile App",
        "Single Sign On",
        "Badge",
      ];
      const deviceCards = [
        "Lockset",
        "Facegate",
        "OSDP Device",
        "Trilogy Lockset",
        "Intercom",
        "Directory Extension",
      ];
      const mobileCredentialCards = [
        "Sicunet Mobile",
        "Wavelynx Mobile",
        "Farpointe Mobile",
        "Mobile Credential",
      ];

      Object.entries(originalLicenseData.optionsData).forEach(
        ([key, originalItem]) => {
          if (hidePriceLabels.includes(key)) return;

          const currentItem = optionsData[key];

          // Only add if the item doesn't exist in current optionsData
          // OR if it's a toggle that was disabled
          const isRemoved =
            !currentItem ||
            (originalItem.type === "toggle" &&
              originalItem.enabled &&
              currentItem?.type === "toggle" &&
              !currentItem.enabled);

          if (isRemoved && !addedKeys.has(key)) {
            const removedItem = {
              name: key,
              ...originalItem,
              isRemoved: true,
            };

            if (optionsCards.includes(key)) {
              groups.options.push(removedItem);
            } else if (deviceCards.includes(key)) {
              groups.device.push(removedItem);
            } else if (key === "Channel") {
              groups.video.push(removedItem);
            } else if (mobileCredentialCards.includes(key)) {
              groups.mobile.push(removedItem);
            } else {
              groups.options.push(removedItem);
            }
            addedKeys.add(key);
          }
        },
      );
    }

    return groups;
  }, [hasAnyChanges, edit, optionsData, originalLicenseData]);

  // Object.entries(optionsData).forEach(([key, val]) => {
  //   // Filter out hidePriceLabels
  //   if (hidePriceLabels.includes(key)) {
  //     return;
  //   }

  //   const item = { name: key, ...val };

  //   // Options group
  //   const optionsCards = [
  //     "REST API",
  //     "Log API",
  //     "Credential Invitation",
  //     "Visitor Invitation",
  //     "Gemini",
  //     "Mobile App",
  //     "Single Sign On",
  //     "Badge",
  //   ];

  //   // Device group
  //   const deviceCards = [
  //     "Lockset",
  //     "Facegate",
  //     "OSDP Device",
  //     "Trilogy Lockset",
  //     "Intercom",
  //     "Directory Extension",
  //   ];

  //   // Mobile Credential group
  //   const mobileCredentialCards = [
  //     "Sicunet Mobile",
  //     "Wavelynx Mobile",
  //     "Farpointe Mobile",
  //     "Mobile Credential",
  //   ];

  //   if (optionsCards.includes(key)) {
  //     groupedOptions.options.push(item);
  //   } else if (deviceCards.includes(key)) {
  //     groupedOptions.device.push(item);
  //   } else if (key === "Channel") {
  //     groupedOptions.video.push(item);
  //   } else if (mobileCredentialCards.includes(key)) {
  //     groupedOptions.mobile.push(item);
  //   } else {
  //     // Fallback to options
  //     groupedOptions.options.push(item);
  //   }
  // });

  // // Add removed items to appropriate groups
  // if (edit && originalLicenseData?.optionsData) {
  //   const optionsCards = [
  //     "REST API",
  //     "Log API",
  //     "Credential Invitation",
  //     "Visitor Invitation",
  //     "Gemini",
  //     "Mobile App",
  //     "Single Sign On",
  //     "Badge",
  //   ];
  //   const deviceCards = [
  //     "Lockset",
  //     "Facegate",
  //     "OSDP Device",
  //     "Trilogy Lockset",
  //     "Intercom",
  //     "Directory Extension",
  //   ];
  //   const mobileCredentialCards = [
  //     "Sicunet Mobile",
  //     "Wavelynx Mobile",
  //     "Farpointe Mobile",
  //     "Mobile Credential",
  //   ];

  //   Object.entries(originalLicenseData.optionsData).forEach(
  //     ([key, originalItem]) => {
  //       // Filter out hidePriceLabels
  //       if (hidePriceLabels.includes(key)) {
  //         return;
  //       }

  //       const currentItem = optionsData[key];

  //       // If item was removed
  //       if (
  //         !currentItem ||
  //         (originalItem.type === "toggle" &&
  //           originalItem.enabled &&
  //           !currentItem.enabled)
  //       ) {
  //         const removedItem = {
  //           name: key,
  //           ...originalItem,
  //           isRemoved: true,
  //         };

  //         // Add to appropriate group
  //         if (optionsCards.includes(key)) {
  //           groupedOptions.options.push(removedItem);
  //         } else if (deviceCards.includes(key)) {
  //           groupedOptions.device.push(removedItem);
  //         } else if (key === "Channel") {
  //           groupedOptions.video.push(removedItem);
  //         } else if (mobileCredentialCards.includes(key)) {
  //           groupedOptions.mobile.push(removedItem);
  //         } else {
  //           groupedOptions.options.push(removedItem);
  //         }
  //       }
  //     },
  //   );
  // }

  const renderSection = (title, items) => {
    if (items.length === 0) return null;

    const getTitleIcon = (title) => {
      if (title === "Model") return <LicenseTypeIcon />;
      if (title === "Site Type") return <SiteTypeIcon />;
      if (title === "Door") return <DoorTypeIcon />;
      return null;
    };

    const titleIcon = getTitleIcon(title);

    return (
      <div className="mb-2 border border-gray-200 rounded-[4px] bg-white overflow-hidden">
        <div className="py-1 bg-gray-50 pr-2.5 pl-2 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-700">{title}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs table-fixed">
            {hasAnyChanges && (
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[25%]">
                    Old Item
                  </th>
                  <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[15%]">
                    Old Price
                  </th>
                  <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[25%]">
                    New Item
                  </th>
                  <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[15%]">
                    New Price
                  </th>
                  <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[15%]">
                    Diff.
                  </th>
                </tr>
              </thead>
            )}
            <tbody>
              {items.map((item, index) => {
                let icon;
                if (
                  title === "Model" ||
                  title === "Door" ||
                  title === "Site Type"
                ) {
                  icon = titleIcon;
                } else {
                  const baseName = item.name.includes("×")
                    ? item.name?.split("×")[1].trim()
                    : item.name;
                  icon = iconMap[baseName] || (
                    <ApiOutlined style={{ fontSize: "14px" }} />
                  );
                }

                // Handle removed items
                if (item.isRemoved) {
                  const displayValue = item.quantity
                    ? `${item.quantity} × ${item.name}`
                    : item.name;

                  return (
                    <tr
                      key={index}
                      className="border-b last:border-b-0 border-gray-200 hover:bg-gray-50"
                    >
                      <td className="py-1 px-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-gray-500 flex-shrink-0"
                            style={{
                              transform: "scale(0.8)",
                              transformOrigin: "center",
                            }}
                          >
                            {icon}
                          </span>
                          <span className="text-gray-500 truncate">
                            {displayValue}
                          </span>
                        </div>
                      </td>
                      <td className="py-1 px-2 text-left text-gray-500">
                        ${Number(item.price).toFixed(2)}
                      </td>
                      <td className="py-1 px-2">
                        <span className="text-gray-400 text-xs">-</span>
                      </td>
                      <td className="py-1 px-2 text-left">
                        <span className="text-gray-400 text-xs">$0.00</span>
                      </td>
                      <td className="py-1 px-2 text-left font-semibold text-red-600">
                        -${Number(item.price).toFixed(2)}
                      </td>
                    </tr>
                  );
                }

                // Get difference for this item
                let hasChange = false;
                let originalValue = null;
                let currentValue = null;
                let originalPrice = null;
                let currentPrice = null;

                if (title === "Model" && originalLicenseData) {
                  const originalModel = modelsOptions?.find(
                    (m) => m.value === originalLicenseData.Model,
                  );
                  const currentModel = modelsOptions?.find(
                    (m) => m.value === mergedData?.Model,
                  );

                  if (
                    originalModel?.label !== currentModel?.label ||
                    originalLicenseData.licenseTypePrice !== licenseTypePrice
                  ) {
                    hasChange = true;
                    originalValue = originalModel?.label;
                    currentValue = currentModel?.label;
                    originalPrice = originalLicenseData.licenseTypePrice;
                    currentPrice = licenseTypePrice;
                  }
                } else if (title === "Site Type" && originalLicenseData) {
                  const originalSite = siteTypeOptions?.find(
                    (m) => m.value === originalLicenseData.SiteType,
                  );
                  const currentSite = siteTypeOptions?.find(
                    (m) => m.value === mergedData?.SiteType,
                  );

                  if (
                    originalSite?.label !== currentSite?.label ||
                    originalLicenseData.siteTypePrice !== siteTypePrice
                  ) {
                    hasChange = true;
                    originalValue = originalSite?.label;
                    currentValue = currentSite?.label;
                    originalPrice = originalLicenseData.siteTypePrice;
                    currentPrice = siteTypePrice;
                  }
                } else if (title === "Door" && originalLicenseData) {
                  if (
                    originalLicenseData.Type !== mergedData?.Type ||
                    originalLicenseData.doorPrice !== doorPrice
                  ) {
                    hasChange = true;
                    originalValue = `${originalLicenseData.Type} × Door`;
                    currentValue = `${mergedData?.Type} × Door`;
                    originalPrice = originalLicenseData.doorPrice;
                    currentPrice = doorPrice;
                  }
                } else {
                  // For options
                  const itemName = item.name.includes("×")
                    ? item.name.split("×")[1].trim()
                    : item.name;
                  const optionDiff = getOptionDifference(itemName, item);

                  if (optionDiff) {
                    hasChange = true;
                    const originalItem =
                      originalLicenseData?.optionsData?.[itemName];

                    if (optionDiff.type === "added") {
                      originalValue = null;
                      currentValue =
                        item.type === "input" || item.type === "select"
                          ? `${item.quantity} × ${itemName}`
                          : itemName;
                      originalPrice = 0;
                      currentPrice = item.price;
                    } else if (
                      optionDiff.type === "increased" ||
                      optionDiff.type === "decreased"
                    ) {
                      originalValue = `${originalItem.quantity} × ${itemName}`;
                      currentValue = `${item.quantity} × ${itemName}`;
                      originalPrice = originalItem.price;
                      currentPrice = item.price;
                    } else if (optionDiff.type === "price_changed") {
                      // Price changed but quantity stayed the same
                      const displayName =
                        item.type === "input" || item.type === "select"
                          ? `${item.quantity} × ${itemName}`
                          : itemName;
                      originalValue = displayName;
                      currentValue = displayName;
                      originalPrice = originalItem.price;
                      currentPrice = item.price;
                    }
                  }
                }

                const itemDisplayName = item.name.includes("×")
                  ? item.name.split("×")[1].trim()
                  : item.name;

                // Render based on whether there's a change and if we're in edit mode
                if (hasChange && hasAnyChanges) {
                  const priceDiff = currentPrice - originalPrice;
                  return (
                    <tr
                      key={index}
                      className="border-b last:border-b-0 border-gray-200 hover:bg-gray-50"
                    >
                      <td className="py-1 px-2">
                        {originalValue !== null ? (
                          <div className="flex items-center gap-2">
                            <span
                              className="text-gray-500 flex-shrink-0"
                              style={{
                                transform: "scale(0.8)",
                                transformOrigin: "center",
                              }}
                            >
                              {icon}
                            </span>
                            <span className="text-gray-500 truncate">
                              {originalValue}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="py-1 px-2 text-left text-gray-500">
                        {originalPrice > 0
                          ? `$${Number(originalPrice).toFixed(2)}`
                          : "-"}
                      </td>
                      <td className="py-1 px-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-gray-700 flex-shrink-0"
                            style={{
                              transform: "scale(0.8)",
                              transformOrigin: "center",
                            }}
                          >
                            {icon}
                          </span>
                          <span className="text-gray-800 font-medium truncate">
                            {currentValue}
                          </span>
                        </div>
                      </td>
                      <td className="py-1 px-2 text-left text-gray-800 font-medium">
                        ${Number(currentPrice).toFixed(2)}
                      </td>
                      <td
                        className={`py-1 px-2 text-left font-semibold ${
                          priceDiff > 0
                            ? "text-green-600"
                            : priceDiff < 0
                              ? "text-red-600"
                              : "text-gray-600"
                        }`}
                      >
                        {priceDiff > 0 ? "+" : ""}
                        {priceDiff < 0 ? "-" : ""}$
                        {Math.abs(priceDiff).toFixed(2)}
                      </td>
                    </tr>
                  );
                } else if (!hasAnyChanges) {
                  // No edit mode - show simple view
                  return (
                    <tr
                      key={index}
                      className="border-b last:border-b-0 border-gray-200"
                    >
                      <td className="py-1 px-2" colSpan="5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className="text-gray-600 flex-shrink-0"
                              style={{
                                transform: "scale(0.8)",
                                transformOrigin: "center",
                              }}
                            >
                              {icon}
                            </span>
                            <span className="text-gray-600">
                              {item.type === "input" || item.type === "select"
                                ? `${item.quantity} × ${item.name}`
                                : item.name}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">
                            ${Number(item.price).toFixed(2)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                } else {
                  // No change in edit mode - show same item in both columns
                  const displayValue =
                    item.type === "input" || item.type === "select"
                      ? `${item.quantity} × ${itemDisplayName}`
                      : itemDisplayName;

                  return (
                    <tr
                      key={index}
                      className="border-b last:border-b-0 border-gray-200 hover:bg-gray-50"
                    >
                      <td className="py-1 px-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-gray-600 flex-shrink-0"
                            style={{
                              transform: "scale(0.8)",
                              transformOrigin: "center",
                            }}
                          >
                            {icon}
                          </span>
                          <span className="text-gray-600 truncate">
                            {displayValue}
                          </span>
                        </div>
                      </td>
                      <td className="py-1 px-2 text-left text-gray-600">
                        ${Number(item.price).toFixed(2)}
                      </td>
                      <td className="py-1 px-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-gray-600 flex-shrink-0"
                            style={{
                              transform: "scale(0.8)",
                              transformOrigin: "center",
                            }}
                          >
                            {icon}
                          </span>
                          <span className="text-gray-600 truncate">
                            {displayValue}
                          </span>
                        </div>
                      </td>
                      <td className="py-1 px-2 text-left text-gray-600">
                        ${Number(item.price).toFixed(2)}
                      </td>
                      <td className="py-1 px-2 text-left text-gray-600">-</td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const onReset = () => {
    onCancel();
  };

  const buttonsData = [
    {
      label: "Apply",
      icon: <CheckCircleOutlined />,
      onClick: onConfirm,
      loading: loading,
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: onReset,
    },
  ];

  const licenseItems = [
    {
      name: getModelName(),
      price: licenseTypePrice,
      type: "label",
    },
  ];

  const siteTypeItems = [
    {
      name: getSiteTypeName(),
      price: siteTypePrice,
      type: "label",
    },
  ];

  const doorItems = [
    {
      name: "Door",
      price: doorPrice,
      type: "label",
      quantity: mergedData?.Type,
    },
  ];

  const totalPrice = calculateTotals(
    optionsData,
    licenseTypePrice,
    doorPrice,
    siteTypePrice,
  );

  return (
    <div className="bg-white rounded-[4px] overflow-hidden flex flex-col max-h-[90vh]">
      {/* Header with License Type Selector */}
      <div className="bg-gray-200 py-1 px-2.5 rounded-[4px] flex items-center justify-between mb-1 flex-shrink-0">
        <div className="flex items-center">
          <LicenseIcon />
          <h2 className="text-sm font-[Poppins] text-black ml-2">
            License Price
          </h2>
        </div>
        <div className="w-24">
          <Select
            value={licenseType}
            onChange={(value) => {
              setLicenseType(value);
              form.setFieldValue("LicenseType", value);
            }}
            disabled={disableSelect}
            options={orderTypeOptions}
            size="small"
            style={{ width: "100%", height: "30px" }}
          />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {siteTypeOptions && renderSection("Site Type", siteTypeItems)}
        {renderSection("Model", licenseItems)}
        {renderSection("Door", doorItems)}

        {/* 4 Groups - matching card layout */}
        {hasAnyChanges && edit ? (
          // Edit mode with changes - show only Options
          renderSection("Options", groupedOptions.options)
        ) : (
          // Normal mode - show all 4 groups
          <>
            {renderSection("Options", groupedOptions.options)}
            {renderSection("Device", groupedOptions.device)}
            {renderSection("Video Integration", groupedOptions.video)}
            {renderSection("Mobile Credential", groupedOptions.mobile)}
          </>
        )}
      </div>

      {/* Sticky Bottom Section */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white">
        {/* Total */}
        <div className="mb-1 border-t border-gray-300 bg-white overflow-hidden">
          {edit &&
            originalLicenseData &&
            (() => {
              const originalTotal = calculateTotals(
                originalLicenseData.optionsData,
                originalLicenseData.licenseTypePrice,
                originalLicenseData.doorPrice,
                originalLicenseData.siteTypePrice,
              );
              const currentTotal = totalPrice;
              const diff = Number(currentTotal) - Number(originalTotal);

              if (Math.abs(diff) > 0.01) {
                return (
                  <table className="w-full text-xs table-fixed">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[25%]"></th>{" "}
                        <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[15%]">
                          Old Total
                        </th>
                        <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[25%]"></th>
                        <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[15%]">
                          New Total
                        </th>
                        <th className="py-1.5 px-2 text-left font-semibold text-gray-700 w-[15%]">
                          Diff.
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-1 px-2 text-left text-gray-600 font-medium"></td>
                        <td className="py-1 px-2 text-left text-gray-600 font-medium">
                          ${Number(originalTotal).toFixed(2)}
                        </td>
                        <td className="py-1 px-2 text-left text-gray-600 font-medium"></td>
                        <td className="py-1 px-2 text-left font-bold text-gray-900">
                          ${Number(currentTotal).toFixed(2)}
                        </td>
                        <td
                          className={`py-1 px-2 text-left font-bold ${
                            diff > 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {diff > 0 ? "+" : ""}
                          {diff < 0 ? "-" : ""}${Math.abs(diff).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                );
              }
              return (
                <div className="flex justify-between items-center py-1 bg-gray-50 px-2.5">
                  <span className="text-xs font-semibold text-gray-800">
                    Total Price
                  </span>
                  <span className="text-sm font-bold text-gray-800">
                    $ {Number(currentTotal).toFixed(2)}
                  </span>
                </div>
              );
            })()}

          {(!edit || !originalLicenseData) && (
            <div className="flex justify-between items-center py-1 bg-gray-50 px-2.5">
              <span className="text-xs font-semibold text-gray-800">
                Total Price
              </span>
              <span className="text-sm font-bold text-gray-800">
                $ {Number(totalPrice).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-center items-center py-1">
          <CommonButton buttonsData={buttonsData} />
        </div>
      </div>
    </div>
  );
};

export default LicenseConfirmForm;
